import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_5309bd56-751f-4453-bda3-7be8ecfcb781/artifacts/orcvyuj1_let%20see%20whos%20on.png";

const LEGAL_LINKS = [
  { to: "/terms",      label: "Terms & Conditions" },
  { to: "/privacy",    label: "Privacy Policy"     },
  { to: "/guidelines", label: "Community Guidelines" },
];

export default function LegalPage({ title, lastUpdated, badge, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#07070B] text-slate-200">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0E0E17]/95 backdrop-blur-xl border-b border-purple-500/20">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src={LOGO_URL} alt="Who's On?" className="h-8 w-auto drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]" />
          <div className="ml-2 h-4 w-px bg-slate-700" />
          <span className="text-sm font-medium text-slate-300">{title}</span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
        {/* Title block */}
        <div className="mb-10 pb-6 border-b border-purple-500/15">
          {badge && (
            <span className="inline-block mb-3 text-[10px] font-mono uppercase tracking-widest text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 rounded-full">
              {badge}
            </span>
          )}
          <h1 className="text-3xl sm:text-4xl font-black text-white font-[Outfit] mb-2">{title}</h1>
          <p className="text-sm text-slate-500">Last updated: {lastUpdated}</p>
        </div>

        {/* Body */}
        <div className="prose-legal">
          {children}
        </div>

        {/* Other legal pages */}
        <div className="mt-16 pt-8 border-t border-purple-500/15">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-600 mb-3">Other legal pages</p>
          <div className="flex flex-wrap gap-3">
            {LEGAL_LINKS.map(l => (
              <Link key={l.to} to={l.to}
                className="text-sm text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ── Reusable prose elements ───────────────────────────────────────────── */
export function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10">
      <h2 className="text-lg font-bold text-white font-[Outfit] mb-3 pb-1.5 border-b border-purple-500/10">
        {title}
      </h2>
      <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function P({ children }) {
  return <p className="text-slate-300 text-sm leading-relaxed">{children}</p>;
}

export function UL({ items }) {
  return (
    <ul className="space-y-1.5 pl-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function Highlight({ children }) {
  return (
    <div className="bg-purple-500/8 border border-purple-500/20 rounded-xl px-4 py-3 text-sm text-slate-300 leading-relaxed">
      {children}
    </div>
  );
}

export function Placeholder({ label }) {
  return (
    <span className="inline-block bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-mono px-1.5 py-0.5 rounded">
      [{label}]
    </span>
  );
}
